import * as Yup from 'yup';
import {
  Origins,
  ScopeThreeSection,
} from 'models/ScopeThreeCategoryOne/ScopeThreeCategoryOne.model';
import { Category1TypeFields } from 'enums/category1Enums';
import { originsValidation } from './originsValidationSchema';
import { FarmboxRawMaterials } from 'models/FarmboxRawMaterials/farmBoxRawMaterials.model';
import {
  MAX_TWO_DECIMALS_REGEX,
  requiredErrorMessage,
} from 'constants/constants';

export interface TestContextExtended {
  from: {
    value: FarmboxRawMaterials;
  }[];
}
// Custom validation function to check if any of the fields are filled, others must be required
export const fieldRequiredIfAnyOtherFieldFilled = (
  isRequired?: boolean
): Yup.TestFunction =>
  function (this, value) {
    const isEmptyValue = value !== undefined && value !== null && value !== '';
    //Making the usual required check
    if (isRequired) return isEmptyValue;
    const { activityLabel, quantity, unit, bestFitRegion, origins } =
      this.parent;
    // Check if any of the fields are filled
    const isAnyFieldFilled = activityLabel || quantity || unit || bestFitRegion;

    // Checking any origin's related field is filled
    const hasOriginFields = origins?.some(
      ({ share, addressLabel, activityMappingRegionSpecId }: Origins) =>
        share != null ||
        addressLabel != null ||
        activityMappingRegionSpecId != null
    );

    if (hasOriginFields || isAnyFieldFilled)
      // If any field is filled, this field must also be filled
      return isEmptyValue;

    return true; // If no field is filled, this field is not required
  };

const createValidationForFarboxRawMaterials = (
  category: keyof ScopeThreeSection
) => {
  const isFertilizers = category === Category1TypeFields.FERTILISERS;
  return Yup.array().of(
    Yup.object().shape({
      activityLabel: Yup.string()
        .required(requiredErrorMessage)
        .typeError(requiredErrorMessage)
        .test(
          'requiredIfAnyFieldFilled',
          'Field is required',
          fieldRequiredIfAnyOtherFieldFilled(isFertilizers)
        )
        .test(
          'unique-activityLabel',
          'Duplicate item selected',
          function (this) {
            const array = (this as Yup.TestContext & TestContextExtended)
              .from[1].value[category] as ScopeThreeSection[]; // Reference to the array of objects for the current category
            const currentItem = (this as Yup.TestContext & TestContextExtended)
              .from[0].value;
            const labels = array
              .filter(item => item.activityLabel !== null)
              .map(item => item.activityLabel) as string[];
            const count = labels.filter(
              label => label === currentItem.activityLabel
            ).length;
            return count <= 1;
          }
        ),
      quantity: Yup.number()
        .min(0, 'Quantity must be greater than or equal to 0')
        .required(requiredErrorMessage)
        .typeError(requiredErrorMessage)
        .test(
          'requiredIfAnyFieldFilled',
          'Field is required',
          fieldRequiredIfAnyOtherFieldFilled(isFertilizers)
        )
        .test(
          'decimal-precision',
          'Value must have at most 2 decimal places',
          value => !value || MAX_TWO_DECIMALS_REGEX.test(value.toString())
        ),
      addressLabel: Yup.string()
        .nullable()
        .test(
          'requiredIfOneOriginFieldFilled',
          'Field is required',
          function (this) {
            const recordData = (this as Yup.TestContext & TestContextExtended)
              .from[0].value;
            const {
              activityLabel,
              activityMappingRegionSpecId,
              unit,
              quantity,
              addressLabel,
              multiOrigin,
            } = recordData;
            const isValidField =
              !multiOrigin &&
              (activityLabel ||
                activityMappingRegionSpecId ||
                unit ||
                quantity) &&
              !addressLabel;
            if (isValidField) {
              return false;
            }
            return true; // Valid if origins.length !== 2
          }
        ),
      activityMappingRegionSpecId: Yup.string()
        .nullable()
        .test('requiredIfOriginsHasTwo', 'Field is required', function (this) {
          const recordData = (this as Yup.TestContext & TestContextExtended)
            .from[0].value;
          const {
            activityLabel,
            addressLabel,
            unit,
            quantity,
            multiOrigin,
            activityMappingRegionSpecId,
          } = recordData;
          const isValidField =
            !multiOrigin &&
            (activityLabel || addressLabel || unit || quantity) &&
            !activityMappingRegionSpecId;
          if (isValidField) {
            return false;
          }
          return true; // Valid if origins.length !== 2
        }),
      unit: Yup.string()
        .required(requiredErrorMessage)
        .typeError(requiredErrorMessage)
        .test(
          'requiredIfAnyFieldFilled',
          'Field is required',
          fieldRequiredIfAnyOtherFieldFilled(isFertilizers)
        ),
      bestFitRegion: Yup.string().nullable(),
      origins: originsValidation,
    })
  );
};

export const validationSchema = Yup.object().shape({
  fertilizers: createValidationForFarboxRawMaterials(
    Category1TypeFields.FERTILISERS
  ),
  packagings: createValidationForFarboxRawMaterials(
    Category1TypeFields.PACKAGING_MATERIALS
  ),
});
